import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import THEME from '../../styles/theme';
import useStore from '../../store/useStore';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -45%);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${THEME.zIndex.modal};
  animation: ${fadeIn} ${THEME.transitionFast};
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  background: ${THEME.panelBackground};
  border: 1px solid ${THEME.border};
  border-radius: 8px;
  width: 95%;
  max-width: 1400px;
  height: 92vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} ${THEME.transitionMedium};
  ${props => props.$isDragging ? 'cursor: grabbing;' : ''}
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${THEME.spacing.sm} ${THEME.spacing.lg};
  border-bottom: 1px solid ${THEME.border};
  cursor: grab;
  user-select: none;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${THEME.fontSize.md};
  color: ${THEME.textPrimary};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${THEME.spacing.sm};
`;

const PartNumber = styled.span`
  color: ${THEME.primary};
  font-family: monospace;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.xl};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all ${THEME.transitionFast};

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: ${THEME.textPrimary};
  }
`;

const PdfContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  background: ${THEME.canvasBackground};
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

const LoadingMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.lg};
  text-align: center;
`;

const FallbackContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: ${THEME.textSecondary};
`;

const FallbackButton = styled.a`
  display: inline-block;
  margin-top: 16px;
  padding: 12px 24px;
  background: ${THEME.primary};
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: background 0.2s;

  &:hover {
    background: ${THEME.primaryHover};
  }
`;

const DatasheetModal = () => {
  const {
    isDatasheetModalOpen,
    currentDatasheetUrl,
    currentDatasheetPartNumber,
    closeDatasheetModal
  } = useStore();

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const modalRef = useRef(null);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isDatasheetModalOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeDatasheetModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDatasheetModalOpen, closeDatasheetModal]);

  // Check if PDF is available when modal opens
  useEffect(() => {
    if (isDatasheetModalOpen && currentDatasheetUrl) {
      setIsLoading(true);
      setLoadError(false);

      // Do a HEAD request to check content-type before loading iframe
      fetch(currentDatasheetUrl, { method: 'HEAD' })
        .then(response => {
          const contentType = response.headers.get('content-type') || '';
          if (!contentType.includes('application/pdf')) {
            // Not a PDF - show fallback immediately
            setLoadError(true);
            setIsLoading(false);
          }
          // If it is PDF, let the iframe load it
        })
        .catch(() => {
          // Network error - let iframe try anyway
        });
    }
  }, [isDatasheetModalOpen, currentDatasheetUrl]);

  const handleMouseDown = (e) => {
    // Only allow dragging from header (not close button)
    if (e.target.closest('button')) return;

    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  if (!isDatasheetModalOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeDatasheetModal();
    }
  };

  const handleIframeLoad = (e) => {
    setIsLoading(false);
    // Check if iframe loaded an error response or HTML instead of PDF
    try {
      const iframe = e.target;
      if (iframe.contentDocument) {
        const body = iframe.contentDocument.body;
        // Check for error JSON or if we got HTML (the app) instead of PDF
        if (body) {
          const text = body.innerText || '';
          const html = body.innerHTML || '';
          if (text.includes('"error"') || html.includes('GChain') || html.includes('<!DOCTYPE')) {
            setLoadError(true);
          }
        }
      }
    } catch (err) {
      // Cross-origin error usually means PDF plugin loaded (which is good)
      // But could also be a same-origin HTML page - check URL
    }
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setLoadError(true);
  };

  // Build fallback URL for direct browser access
  const getFallbackUrl = () => {
    if (!currentDatasheetPartNumber) return '#';
    const sku = currentDatasheetPartNumber.replace(/^GRF/, '');
    return `https://www.guerrilla-rf.com/products/DataSheet?sku=${sku}&file_name=${currentDatasheetPartNumber}DS.pdf`;
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        $isDragging={isDragging}
        style={{
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
        }}
      >
        <ModalHeader onMouseDown={handleMouseDown}>
          <ModalTitle>
            📄 Data Sheet - <PartNumber>{currentDatasheetPartNumber}</PartNumber>
          </ModalTitle>
          <CloseButton onClick={closeDatasheetModal} aria-label="Close">
            ✕
          </CloseButton>
        </ModalHeader>

        <PdfContainer>
          {isLoading && <LoadingMessage>Loading datasheet...</LoadingMessage>}
          {loadError && (
            <FallbackContainer>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>Unable to load datasheet in app</div>
              <div style={{ fontSize: '14px', opacity: 0.7 }}>The PDF server requires browser authentication</div>
              <FallbackButton href={getFallbackUrl()} target="_blank" rel="noopener noreferrer">
                Open in New Tab
              </FallbackButton>
            </FallbackContainer>
          )}
          {!loadError && (
            <StyledIframe
              src={currentDatasheetUrl}
              title={`${currentDatasheetPartNumber} Datasheet`}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              style={{ display: isLoading ? 'none' : 'block' }}
            />
          )}
        </PdfContainer>
      </Modal>
    </Overlay>
  );
};

export default DatasheetModal;
